/* ============================================================
   Daytakt — the systems band.

   Two jobs, kept apart:

   1. THE PLATE. Each slide owns the selected/deselected state of its own
      objects and the four strings that state produces (status, saving,
      total, button). Everything recomputes synchronously on every toggle
      — no debounce — because the price is the reason the section exists
      and a lagging number reads as a bug.

   2. THE CAROUSEL. A scroll container with snap points does the actual
      moving; this only keeps the name row and the rail in step with it,
      and lets a click on a name or an arrow scroll it. No transform
      maths, no library — with JavaScript off the band is still a
      swipeable row of systems.

   The saving is never applied here. It is the store's ladder, two
   Shopify automatic discounts (snippets/nx-ladder.liquid), and this
   script only has to agree with what the cart will do. Adding goes
   through window.nxCart so the drawer is repainted rather than opened
   stale — see nx-quick-add.js.
   ============================================================ */
(function () {
  'use strict';

  /* The currency sign of the market the visitor is in (2026-11-02, UK): Shopify prints the presentment
     currency in Shopify.currency.active; Liquid's money filter already uses it, this keeps the script in step. */
  var SYM = ({ GBP: '£', EUR: '€' })[(window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || ''] || '$';

  function formatMoney(cents, format) {
    var value = (cents / 100).toFixed(2);
    var parts = value.split('.');
    var whole = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    var withCents = whole + '.' + parts[1];
    if (format && format.indexOf('{{') !== -1) {
      return format
        .replace(/\{\{\s*amount\s*\}\}/g, withCents)
        .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, whole)
        .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, withCents)
        .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g, whole);
    }
    return SYM + withCents;
  }

  /* No price in this shop has cents, so a trailing ".00" is noise — but a
     discounted TOTAL legitimately has them and must keep them. The test is
     on the NUMBER, never a regex over the formatted string: that was tried
     twice and wrong twice, once with an unescaped dot and once with an
     invisible backspace byte. */
  function money(cents, format) {
    var s = formatMoney(cents, format);
    return cents % 100 === 0 ? s.replace('.00', '') : s;
  }

  /* The same amount with small cents (owner, 2026-09-30), as snippets/nx-money.liquid prints it: the
     cents come from the number, and the result is written with innerHTML. */
  function moneyHTML(cents) {
    var c = cents % 100;
    var whole = String((cents - c) / 100).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return SYM + whole + (c ? '<sup class="nx-cents">' + (c < 10 ? '0' : '') + c + '</sup>' : '');
  }

  /* ---- one slide's plate ------------------------------------------ */
  function wirePlate(slide, ladder, moneyFormat, baseLabel) {
    var inputs = Array.prototype.slice.call(slide.querySelectorAll('[data-nx-set-item]'));
    var statusEl = slide.querySelector('[data-nx-set-status]');
    var countEl = slide.querySelector('[data-nx-set-count]');
    var totalEl = slide.querySelector('[data-nx-set-total]');
    var compareEl = slide.querySelector('[data-nx-set-compare]');
    var addEl = slide.querySelector('[data-nx-set-add]');
    if (!inputs.length || !addEl) return;

    var sellable = inputs.filter(function (i) { return !i.disabled; });
    var failed = false;

    function rateFor(n) {
      if (n >= 4) return ladder[2];
      if (n === 3) return ladder[1];
      if (n === 2) return ladder[0];
      return 0;
    }

    function selected() {
      return sellable.filter(function (i) { return i.checked; });
    }

    function render() {
      var picked = selected();
      var n = picked.length;
      var sum = picked.reduce(function (acc, i) {
        return acc + (parseInt(i.getAttribute('data-price'), 10) || 0);
      }, 0);

      /* The ladder answers to the COUNT, not to "is this the whole
         system", so taking an object out re-prices the plate instead of
         switching the saving off — the reader watches 20% become 15%,
         which teaches the rule better than fine print. */
      var rate = rateFor(n);
      var off = Math.round((sum * rate) / 100);

      totalEl.innerHTML = moneyHTML(sum - off);
      compareEl.hidden = rate === 0;
      compareEl.innerHTML = moneyHTML(sum);

      if (failed) {
        statusEl.textContent = 'Could not add to cart · try again';
        statusEl.classList.add('is-active');
      } else if (rate > 0) {
        statusEl.textContent = n + ' objects · −' + rate + '%';
        statusEl.classList.add('is-active');
      } else if (n === 0) {
        statusEl.textContent = 'Select at least one object';
        statusEl.classList.remove('is-active');
      } else {
        statusEl.textContent = 'Add one more object to save ' + ladder[0] + '%';
        statusEl.classList.remove('is-active');
      }

      countEl.innerHTML = rate > 0
        ? 'Save ' + moneyHTML(off)
        : n + ' of ' + sellable.length + ' selected';

      if (n === 0) {
        addEl.textContent = 'Select an object';
        addEl.disabled = true;
      } else {
        addEl.disabled = false;
        // one span: .nxh-btn is an inline-flex with a gap, and the cents would stand apart as an item
        addEl.innerHTML = '<span>' + (rate > 0
          ? baseLabel + ' — save ' + moneyHTML(off)
          : 'Add ' + n + ' object' + (n === 1 ? '' : 's') + ' — ' + moneyHTML(sum)) + '</span>';
      }
    }

    inputs.forEach(function (input) {
      input.addEventListener('change', function () {
        failed = false;
        render();
      });
    });

    /* The cell is no longer a <label> — a link inside one toggles the
       control as well as following itself, and this cell carries both
       jobs. So the toggle is bound here, and any click that started
       inside the product's name is left alone to navigate. */
    /* 2026-09-18 (owner): the row opens its product and the box alone composes the bundle. Taps on a
       row's picture and reason (Clarity's heatmap of the-curve) were readers reaching for the product,
       and every one of them quietly took that object out of the set instead. The box's target is a
       finger's, 44px around the square (nx-set.liquid). */
    slide.querySelectorAll('[data-nx-set-cell]').forEach(function (cell) {
      cell.addEventListener('click', function (e) {
        var box = cell.querySelector('[data-nx-set-item]');
        var link = cell.querySelector('[data-nx-set-link]');
        if (e.target.closest('[data-nx-set-link]')) return;   // the name is a real link already
        // ⚠ The input toggles itself when the keyboard presses it, and that click bubbles here: toggling
        // it again undid every keyboard choice. Its own change listener above does the rest.
        if (e.target === box) return;
        if (e.target.closest('.nxsys__box')) {
          if (!box || box.disabled) return;
          box.checked = !box.checked;
          failed = false;
          render();
          return;
        }
        if (link) link.click();
      });
    });

    addEl.addEventListener('click', function () {
      var picked = selected();
      if (!picked.length || addEl.getAttribute('aria-busy') === 'true') return;

      var items = picked.map(function (i) {
        return { id: parseInt(i.getAttribute('data-variant'), 10), quantity: 1 };
      });
      var restore = addEl.textContent;
      addEl.setAttribute('aria-busy', 'true');
      addEl.textContent = 'Adding…';

      add(items)
        .then(function () {
          failed = false;
          addEl.removeAttribute('aria-busy');
          render();
        })
        .catch(function () {
          failed = true;
          addEl.removeAttribute('aria-busy');
          addEl.textContent = restore;
          render();
        });
    });

    render();
  }

  /* ⚠ The drawer has to be REPAINTED, not just opened. Dawn's cart-drawer
     holds whatever markup was rendered with the page; on a first visit
     that is the empty cart, so the objects went in and the reader was
     shown a drawer saying there was nothing there. window.nxCart asks for
     the cart-drawer section back with the add. */
  function add(items) {
    if (window.nxCart) return window.nxCart.add(items, items.length + ' added to your cart');
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: items })
    }).then(function (res) {
      if (!res.ok) throw new Error('add failed');
      window.location.href = '/cart';
    });
  }

  /* ---- the carousel ------------------------------------------------ */
  function wireCarousel(root) {
    var track = root.querySelector('[data-nx-set-track]');
    if (!track) return;
    var slides = Array.prototype.slice.call(track.querySelectorAll('[data-nx-set-slide]'));
    if (slides.length < 2) return;

    /* Two controls say the same thing and both carry data-nx-set-go: the
       chips at the head and the rail underneath. One handler drives them,
       but they are painted apart — the chip row is also scrolled to keep
       the current chip in view, and a rail segment has no row to scroll. */
    var goers = Array.prototype.slice.call(root.querySelectorAll('[data-nx-set-go]'));
    var names = Array.prototype.slice.call(root.querySelectorAll('.nxsys__name-btn'));
    var segs = Array.prototype.slice.call(root.querySelectorAll('.nxsys__rail-seg'));
    var steps = Array.prototype.slice.call(root.querySelectorAll('[data-nx-set-step]'));
    var at = 0;

    function paint(i, userDriven) {
      at = i;
      names.forEach(function (b, k) {
        b.classList.toggle('is-current', k === i);
        b.setAttribute('aria-selected', k === i ? 'true' : 'false');
      });
      segs.forEach(function (s, k) {
        s.classList.toggle('is-current', k === i);
        s.setAttribute('aria-selected', k === i ? 'true' : 'false');
      });
      steps.forEach(function (b) {
        var d = parseInt(b.dataset.nxSetStep, 10);
        b.disabled = (d < 0 && i === 0) || (d > 0 && i === slides.length - 1);
      });
      /* ⚠ Never scrollIntoView here. It scrolls EVERY scrollable ancestor,
         including the document — so the first paint on page load dragged
         the homepage down past the masthead to wherever this band happens
         to sit. The owner opened the site and landed on the philosophy
         band. Scroll the name row itself, horizontally, and nothing else. */
      var current = names[i];
      var row = current && current.parentElement;
      if (current && row && row.scrollWidth > row.clientWidth) {
        var left = current.offsetLeft - (row.clientWidth - current.clientWidth) / 2;
        row.scrollTo({ left: Math.max(0, left), behavior: userDriven ? 'smooth' : 'auto' });
      }
    }

    /* ⚠ Leaving for a product and pressing Back used to land on the FIRST
       system, not the one you left from — the plate is stateless by
       design, which is right for the selection but wrong for the choice
       of set. The index is remembered per section id, for this tab only:
       sessionStorage, so it never leaks into a later visit and never
       needs clearing. */
    var memory = 'nx-set:' + root.id;

    function remember(i) {
      try { sessionStorage.setItem(memory, String(i)); } catch (e) { /* private mode */ }
    }

    /* ⚠ offsetLeft is measured from the track's PADDING BOX, and the track
       carries a gutter of padding on the phone so a swipe can start at the
       screen edge. Scrolling to a raw offsetLeft therefore landed every
       slide one gutter to the left — the owner's screenshot had the
       headline's first letter off-screen. Subtract the padding, and clamp
       to the real end of the track so the last slide has a target it can
       actually reach. */
    function leftOf(i) {
      var pad = parseFloat(getComputedStyle(track).paddingLeft) || 0;
      var want = slides[i].offsetLeft - track.offsetLeft - pad;
      var max = track.scrollWidth - track.clientWidth;
      return Math.max(0, Math.min(want, max));
    }

    /* ⚠ THE BLINKING. A smooth scroll emits a scroll event every frame, and
       the listener below repaints from whatever slide is nearest at that
       moment — so travelling from the first system to the fifth lit every
       chip in between, in turn, and each one ran its own 0.22s colour
       transition. The owner: "the selector blinks through all the pills
       until it reaches the fifth".

       While a move of ours is in flight the scroll is not news: we already
       know where it is going and have already painted it. `flying` holds
       the target until the track arrives at it, and a guard timer clears
       it if the scroll is interrupted or never lands. */
    var flying = -1;
    var guard = null;

    function fly(px) {
      flying = px;
      clearTimeout(guard);
      guard = setTimeout(function () { flying = -1; }, 900);
    }

    track.addEventListener('scrollend', function () { flying = -1; clearTimeout(guard); });

    function go(i, how) {
      i = Math.max(0, Math.min(slides.length - 1, i));
      remember(i);
      var px = leftOf(i);
      fly(px);
      track.scrollTo({ left: px, behavior: how || 'smooth' });
      paint(i, how !== 'auto');
    }

    /* The rail already tells you which system you are on; a thing that
       reports a position and cannot set it is half a control. Both it and
       the chips are real buttons in the markup now, so there is no role,
       no tabindex and no keydown handler to bolt on here. */
    goers.forEach(function (b) {
      b.addEventListener('click', function () { go(parseInt(b.dataset.nxSetGo, 10) || 0); });
    });
    steps.forEach(function (b) {
      b.addEventListener('click', function () { go(at + (parseInt(b.dataset.nxSetStep, 10) || 0)); });
    });

    /* The scroll container is the source of truth, not our own counter:
       a swipe, a trackpad flick and a keyboard scroll all move it without
       going through go(), and the name row has to follow those too. */
    var ticking = false;
    track.addEventListener('scroll', function () {
      if (flying >= 0) {
        // arrived: hand control back to the reader
        if (Math.abs(track.scrollLeft - flying) < 2) { flying = -1; clearTimeout(guard); }
        return;
      }
      if (ticking) return;
      ticking = true;
      setTimeout(function () {
        ticking = false;
        var pad0 = parseFloat(getComputedStyle(track).paddingLeft) || 0;
        var mid = track.scrollLeft + track.clientWidth / 2;
        var nearest = 0;
        var best = Infinity;
        slides.forEach(function (s, k) {
          var centre = s.offsetLeft - track.offsetLeft - pad0 + s.clientWidth / 2;
          var d = Math.abs(centre - mid);
          if (d < best) { best = d; nearest = k; }
        });
        if (nearest !== at) { remember(nearest); paint(nearest, true); }
      }, 90);
    }, { passive: true });

    /* ⚠ RESTORE ONLY ON A BACK. The index was being restored on every load,
       so a reader who reloaded the homepage landed on the sixth system —
       the owner reported it as a bug and it is one: a reload is a request
       for the page, not for where you were in it. The Navigation Timing
       entry knows the difference. `back_forward` is the case the memory was
       written for; `navigate` and `reload` start at the beginning.

       And it lands STATICALLY. `behavior: 'auto'`, no smooth chip-row
       scroll, and the move is registered with fly() so the scroll it causes
       does not repaint anything on the way. */
    var nav = (window.performance && performance.getEntriesByType && performance.getEntriesByType('navigation')[0]) || null;
    var resume = 0;
    if (nav && nav.type === 'back_forward') {
      try { resume = parseInt(sessionStorage.getItem(memory), 10) || 0; } catch (e) { resume = 0; }
      if (resume < 0 || resume >= slides.length) resume = 0;
    }
    if (resume > 0) {
      var px = leftOf(resume);
      fly(px);
      track.scrollTo({ left: px, behavior: 'auto' });
    }
    paint(resume, false);
  }

  /* ---- one pair per reader, and a record of which ----------------------
     A product can belong to five pairs. Showing all five turns the band
     back into the thing it replaced — a rail of offers to browse — and
     five headlines that all say "Both live on the desk." read as a
     duplicate rather than a choice. So the page draws ONE.

     ⚠ Which one is a coin toss with weights, not a preference, because
     the point is to find out what sells. And the toss is remembered for
     the session: a reader who comes back to the same product must not
     find a different pair each time, or the page looks broken and the
     data counts one person as several.

     ⚠ The test is on the KIND of pair, not on the pair. At four visits a
     day per product, two pairs of one product need 578 days to separate;
     the same question asked as "equal, cheaper or dearer" over the whole
     catalogue is answered in 39. That is why `arm` travels with the
     event and the pair's own id rides along for later. */
  function send(name, params) {
    /* gtag is injected by the Shopify app after this deferred file runs, so it is looked up at
       send time; dataLayer takes the event if the function still is not there. */
    try {
      if (typeof window.gtag === 'function') { window.gtag('event', name, params); return; }
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push(Object.assign({ event: name }, params));
    } catch (e) { /* analytics must never break the band */ }
  }

  function promoParams(slide, root) {
    return {
      promotion_id: 'pair:' + (slide.getAttribute('data-nx-pair') || 'unknown'),
      promotion_name: slide.getAttribute('data-nx-arm') || 'unset',
      creative_slot: root.getAttribute('data-nx-set-source') === 'product' ? 'pdp_band' : 'home_band',
      items: [{ item_id: slide.getAttribute('data-nx-partner') || '', item_name: slide.getAttribute('aria-label') || '' }],
    };
  }

  function rotate(root) {
    var slides = [].slice.call(root.querySelectorAll('[data-nx-set-slide]'));
    if (!slides.length) return slides;
    if (root.getAttribute('data-nx-set-source') !== 'product' || slides.length < 2) return slides;

    var key = 'nx-pair:' + (root.getAttribute('data-nx-set-product') || location.pathname);
    var chosen = null;
    try { chosen = sessionStorage.getItem(key); } catch (e) { /* private window: toss every time */ }

    var pick = chosen && slides.filter(function (s) { return s.getAttribute('data-nx-pair') === chosen; })[0];
    if (!pick) {
      var total = 0;
      slides.forEach(function (s) { total += Math.max(0, parseInt(s.getAttribute('data-nx-weight'), 10) || 0); });
      if (total <= 0) { pick = slides[0]; } else {
        var roll = Math.random() * total;
        for (var i = 0; i < slides.length; i++) {
          roll -= Math.max(0, parseInt(slides[i].getAttribute('data-nx-weight'), 10) || 0);
          if (roll <= 0) { pick = slides[i]; break; }
        }
        pick = pick || slides[slides.length - 1];
      }
      try { sessionStorage.setItem(key, pick.getAttribute('data-nx-pair') || ''); } catch (e) {}
    }

    slides.forEach(function (s) { if (s !== pick) s.remove(); });
    /* with one pair there is nothing to navigate between */
    root.querySelectorAll('.nxsys__nav, .nxsys__rail').forEach(function (el) { el.remove(); });
    return [pick];
  }

  function setup(root) {
    if (root.__nxSet) return;
    root.__nxSet = true;

    var moneyFormat = root.getAttribute('data-money-format') || '${{amount}}';
    var baseLabel = root.getAttribute('data-button-label') || 'Add the system';

    /* The ladder is read from the DOM: the rates live in
       snippets/nx-ladder.liquid and in the Shopify automatic discounts,
       and a copy written into a script is one place too many to keep in
       step. The three values are the rate at 2, at 3 and at 4 or more —
       since 2026-09-22 the last two are the same number. */
    var ladder = (root.getAttribute('data-nx-ladder') || '20,30,30')
      .split(',')
      .map(function (n) { return parseInt(n, 10) || 0; });

    /* the toss happens BEFORE the plates are wired, so the ones that lost are never wired at all */
    var shown = rotate(root);
    shown.forEach(function (slide) {
      wirePlate(slide, ladder, moneyFormat, baseLabel);
      send('view_promotion', promoParams(slide, root));
      var add = slide.querySelector('[data-nx-set-add]');
      if (add) add.addEventListener('click', function () { send('select_promotion', promoParams(slide, root)); });
    });
    wireCarousel(root);
  }

  function init() {
    document.querySelectorAll('[data-nx-set]').forEach(setup);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // the theme editor re-renders sections in place
  document.addEventListener('shopify:section:load', init);
})();
